/**
 * ============================================================================
 * ProductionFormulas.jsx
 * ============================================================================
 *
 * Página de Gerenciamento de Fórmulas de Produção por Produto
 *
 * Features:
 * - Gerenciar fórmulas por produto (ou fórmula global)
 * - Criar, editar e deletar fórmulas
 * - Ativar/desativar fórmulas por produto
 * - Histórico de renderização (última atualização)
 * - Timer de renderização automática (a cada 1 hora)
 * - Helper colapsível com documentação
 * - Modal de confirmação para deletar
 *
 * ============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculator,
  faPlus,
  faEdit,
  faTrash,
  faCheck,
  faTimes,
  faInfoCircle,
  faSpinner,
  faChevronDown,
  faChevronUp,
  faClock,
  faSync,
  faDatabase,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import productionConfigService from '../../services/productionConfigService';
import { productService } from '../../services/product';
import FormulaHelpGuide from '../../components/FormulaHelpGuide';
import { cacheService } from '../../services/cache';

const ProductionFormulas = () => {
  // ========================================================================
  // STATE
  // ========================================================================

  const [formulas, setFormulas] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Formulário de criação/edição
  const [selectedProduct, setSelectedProduct] = useState('');
  const [formulaInput, setFormulaInput] = useState('');
  const [formulaName, setFormulaName] = useState('');
  const [formulaDescription, setFormulaDescription] = useState('');
  const [editingFormulaId, setEditingFormulaId] = useState(null);

  // Modal de confirmação para deletar
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formulaToDelete, setFormulaToDelete] = useState(null);

  // Diagnóstico de fórmula com dados reais, sem persistir alterações.
  const [formulaToDebug, setFormulaToDebug] = useState(null);
  const [debugProductId, setDebugProductId] = useState('');
  const [debugResult, setDebugResult] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);

  // Helper colapsível
  const [showHelper, setShowHelper] = useState(false);

  // Timer de renderização
  const [lastRenderTime, setLastRenderTime] = useState(null);
  const [nextRenderTime, setNextRenderTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Estados para timestamp de sugestões
  const [suggestionsLastUpdated, setSuggestionsLastUpdated] = useState(null);
  const [suggestionsRefreshCount, setSuggestionsRefreshCount] = useState(0);

  // Estados para gerenciamento de cache
  const [cacheInfo, setCacheInfo] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheUpdating, setCacheUpdating] = useState(false);

  // Refs para o campo de fórmula
  const formulaInputRef = useRef(null);

  // ========================================================================
  // EFFECTS
  // ========================================================================

  useEffect(() => {
    fetchData();
    // Timer para atualizar countdown
    const timer = setInterval(() => {
      updateTimeRemaining();
    }, 1000);
    return () => clearInterval(timer);
  }, [lastRenderTime]);

  // Timer para atualizar informações do cache periodicamente
  useEffect(() => {
    const cacheTimer = setInterval(() => {
      if (!cacheUpdating && !cacheLoading) {
        fetchCacheInfo();
      }
    }, 30000); // A cada 30 segundos

    return () => clearInterval(cacheTimer);
  }, [cacheUpdating, cacheLoading]);

  // ========================================================================
  // DATA FETCHING
  // ========================================================================

  const fetchSuggestionsTimestamp = async () => {
    try {
      // Buscar info do cache de sugestões
      const response = await fetch('/api/production/suggestions/cache-info', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.cache_info && data.cache_info.status === 'valid') {
          const dateToSet = data.cache_info.calculated_at || data.cache_info.cached_at;
          if (dateToSet) {
            const parsedDate = new Date(dateToSet);
            if (!isNaN(parsedDate.getTime())) {
              setSuggestionsLastUpdated(parsedDate);
              setSuggestionsRefreshCount(prev => prev + 1);
            }
          }
        }
      }
    } catch (error) {
      console.warn('Erro ao buscar timestamp de sugestões:', error);
      // Erro silencioso - não mostrar toast
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [formulasData, productsData] = await Promise.all([
        productionConfigService.getFormulas(),
        productService.getProducts(),
      ]);
      setFormulas(formulasData || []);
      setProducts(productsData || []);

      // Buscar timestamp das sugestões
      await fetchSuggestionsTimestamp();

      // Buscar informações do cache
      await fetchCacheInfo();
    } catch (error) {
      toast.error(`Erro ao carregar dados: ${error.message}`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Buscar informações do cache
  const fetchCacheInfo = async () => {
    try {
      setCacheLoading(true);
      const info = await cacheService.getCacheInfo();
      setCacheInfo(info);
    } catch (error) {
      console.error('Erro ao buscar informações do cache:', error);
      setCacheInfo(null);
    } finally {
      setCacheLoading(false);
    }
  };

  // Atualizar cache manualmente
  const handleUpdateCache = async () => {
    try {
      setCacheUpdating(true);
      toast.loading('Atualizando cache...', { id: 'cache-update' });

      await cacheService.updateCache(true);

      toast.success('Cache atualizado! Aguarde alguns segundos...', { id: 'cache-update' });

      // Aguardar 3 segundos e buscar info atualizada
      setTimeout(async () => {
        await fetchCacheInfo();
      }, 3000);
    } catch (error) {
      console.error('Erro ao atualizar cache:', error);
      toast.error(`Erro: ${error.message}`, { id: 'cache-update' });
    } finally {
      setCacheUpdating(false);
    }
  };

  const updateTimeRemaining = () => {
    if (!lastRenderTime) return;

    const last = new Date(lastRenderTime);
    const next = new Date(last.getTime() + 60 * 60 * 1000); // 1 hora
    const now = new Date();
    const remaining = next - now;

    setNextRenderTime(next);
    if (remaining > 0) {
      const minutes = Math.floor(remaining / 1000 / 60);
      const seconds = Math.floor((remaining / 1000) % 60);
      setTimeRemaining({ minutes, seconds });
    } else {
      setTimeRemaining(null);
    }
  };

  const formatTimeRemaining = (time) => {
    if (!time) return 'Atualizar agora';
    return `${time.minutes}m ${time.seconds}s`;
  };

  // ========================================================================
  // VARIABLE INSERTION HANDLER
  // ========================================================================

  const handleInsertVariable = (variableName) => {
    if (!formulaInputRef.current) return;

    const input = formulaInputRef.current;
    const start = input.selectionStart;
    const end = input.selectionEnd;

    // Adicionar espaço se necessário
    const beforeText = formulaInput.substring(0, start);
    const afterText = formulaInput.substring(end);
    const shouldAddSpaceBefore = start > 0 && !beforeText.endsWith(' ') && !beforeText.endsWith('(');
    const shouldAddSpaceAfter = end < formulaInput.length && !afterText.startsWith(' ') && !afterText.startsWith(')');

    const spaceBefore = shouldAddSpaceBefore ? ' ' : '';
    const spaceAfter = shouldAddSpaceAfter ? ' ' : '';

    const newFormula = beforeText + spaceBefore + variableName + spaceAfter + afterText;
    setFormulaInput(newFormula);

    // Focus no input, coloca cursor depois da variável inserida e scroll até ela
    setTimeout(() => {
      if (formulaInputRef.current) {
        const newPosition = start + spaceBefore.length + variableName.length + spaceAfter.length;

        // Scroll o input para tornar o texto visível
        formulaInputRef.current.focus();
        formulaInputRef.current.setSelectionRange(newPosition, newPosition);

        // Forçar scroll para o elemento
        const textareaRect = formulaInputRef.current.getBoundingClientRect();
        if (textareaRect.top < 0) {
          formulaInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 0);

    // Toast para feedback
    toast.success(`✨ Variável "${variableName}" inserida na fórmula!`, {
      duration: 2000,
      position: 'bottom-right',
    });
  };

  // ========================================================================
  // FORM HANDLERS
  // ========================================================================

  const handleResetForm = () => {
    setSelectedProduct('');
    setFormulaInput('');
    setFormulaName('');
    setFormulaDescription('');
    setEditingFormulaId(null);
  };

  const handleEditFormula = (formula) => {
    setSelectedProduct(formula.product_id || '');
    setFormulaInput(formula.formula);
    setFormulaName(formula.name);
    setFormulaDescription(formula.description || '');
    setEditingFormulaId(formula.id);
    // Scroll para o topo
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmitFormula = async (e) => {
    e.preventDefault();

    try {
      if (!formulaName.trim() || !formulaInput.trim()) {
        toast.error('Nome e fórmula são obrigatórios');
        return;
      }

      setSaving(true);

      const formulaData = {
        product_id: selectedProduct || null,
        name: formulaName,
        description: formulaDescription,
        formula: formulaInput,
      };

      if (editingFormulaId) {
        await productionConfigService.updateFormula(editingFormulaId, formulaData);
        toast.success('Fórmula atualizada com sucesso!');
      } else {
        await productionConfigService.createFormula(formulaData);
        toast.success('Fórmula criada com sucesso!');
      }

      handleResetForm();
      await fetchData();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar fórmula');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFormula = (formula) => {
    setFormulaToDelete(formula);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!formulaToDelete) return;

    try {
      await productionConfigService.deleteFormula(formulaToDelete.id);
      toast.success('Fórmula deletada com sucesso!');
      setShowDeleteConfirm(false);
      setFormulaToDelete(null);
      await fetchData();
    } catch (error) {
      toast.error(error.message || 'Erro ao deletar fórmula');
      console.error(error);
    }
  };

  const handleActivateFormula = async (id) => {
    try {
      await productionConfigService.activateFormula(id);
      toast.success('Fórmula ativada com sucesso!');
      await fetchData();
    } catch (error) {
      toast.error(error.message || 'Erro ao ativar fórmula');
      console.error(error);
    }
  };

  const handleDeactivateFormula = async (id) => {
    try {
      await productionConfigService.deactivateFormula(id);
      toast.success('Fórmula desativada com sucesso!');
      await fetchData();
    } catch (error) {
      toast.error(error.message || 'Erro ao desativar fórmula');
      console.error(error);
    }
  };

  const handleOpenFormulaDebug = (formula) => {
    setFormulaToDebug(formula);
    setDebugProductId(formula.product_id || '');
    setDebugResult(null);
  };

  const handleRunFormulaDebug = async () => {
    if (!formulaToDebug || !debugProductId) {
      toast.error('Selecione um produto para diagnosticar a fórmula');
      return;
    }

    try {
      setDebugLoading(true);
      const result = await productionConfigService.debugFormula(
        formulaToDebug.formula,
        debugProductId
      );
      setDebugResult(result);
    } catch (error) {
      toast.error(error.message || 'Erro ao diagnosticar fórmula');
    } finally {
      setDebugLoading(false);
    }
  };

  const handleUpdateSuggestions = async (formula, formulaName) => {
    try {
      const loadingToast = toast.loading('Testando fórmula e atualizando sugestões...');
      const result = await productionConfigService.testFormula(formula);

      if (result.success) {
        toast.dismiss(loadingToast);
        toast.success(`✅ Fórmula "${formulaName}" validada com sucesso!\n\nSugestão Testada: ${result.result || 0} kg\n\nAs sugestões foram atualizadas para todos os produtos.`);
        // Recarregar dados após atualizar sugestões
        setTimeout(() => {
          fetchData();
        }, 1000);
      } else {
        toast.dismiss(loadingToast);
        toast.error(`❌ Erro ao validar fórmula:\n\n${result.error}`);
      }
    } catch (error) {
      toast.error(error.message || 'Erro ao atualizar sugestões');
      console.error(error);
    }
  };

  // ========================================================================
  // RENDER HELPER
  // ========================================================================

  const renderHelper = () => {
    return (
      <div className="mb-4 border border-neutral-200 rounded-lg overflow-hidden">
        <button
          type="button"
          className="w-full px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium flex items-center justify-between transition-colors"
          onClick={() => setShowHelper(!showHelper)}
        >
          <span className="flex items-center gap-2">
            <FontAwesomeIcon icon={faInfoCircle} />
            {showHelper ? 'Ocultar' : 'Mostrar'} Guia de Fórmulas
          </span>
          <FontAwesomeIcon icon={showHelper ? faChevronUp : faChevronDown} size="sm" />
        </button>

        {showHelper && (
          <div className="p-4 bg-white border-t border-neutral-200">
            <FormulaHelpGuide onVariableClick={handleInsertVariable} />
          </div>
        )}
      </div>
    );
  };

  const renderTableSection = () => {
    if (formulas.length === 0) {
      return (
        <div className="text-center py-8 text-neutral-500">
          <FontAwesomeIcon icon={faCalculator} size="2x" className="mb-2" />
          <p className="text-sm">Nenhuma fórmula configurada ainda</p>
        </div>
      );
    }

    return (
      <div className="space-y-2 w-full">
        {formulas.map((formula) => (
          <div
            key={formula.id}
            className="border border-neutral-200 rounded-lg p-3 hover:bg-neutral-50 transition-colors w-full"
          >
            <div className="flex items-start justify-between gap-2 w-full">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h4 className="text-sm font-semibold text-neutral-900 flex-shrink-0">
                    {formula.name}
                  </h4>
                  {formula.is_active && (
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded flex-shrink-0">
                      Ativa
                    </span>
                  )}
                  {formula.product_id ? (
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded flex-shrink-0">
                      {products.find((p) => p.id === formula.product_id)?.name ||
                        'Produto não encontrado'}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 bg-neutral-200 text-neutral-700 rounded flex-shrink-0">
                      Global
                    </span>
                  )}
                </div>
                {formula.description && (
                  <p className="text-xs text-neutral-600 mb-2">{formula.description}</p>
                )}
                <code className="text-xs bg-neutral-100 px-2 py-1 rounded block break-all text-neutral-700">
                  {formula.formula}
                </code>
              </div>
              <div className="flex gap-1 flex-shrink-0 ml-2">
                <button
                  onClick={() => handleEditFormula(formula)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Editar"
                >
                  <FontAwesomeIcon icon={faEdit} size="sm" />
                </button>
                <button
                  onClick={() => handleDeleteFormula(formula)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Deletar"
                >
                  <FontAwesomeIcon icon={faTrash} size="sm" />
                </button>
                <button
                  onClick={() => handleUpdateSuggestions(formula.formula, formula.name)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Testar fórmula"
                >
                  <FontAwesomeIcon icon={faSync} size="sm" />
                </button>
                <button
                  onClick={() => handleOpenFormulaDebug(formula)}
                  className="p-2 text-violet-600 hover:bg-violet-50 rounded transition-colors"
                  title="Diagnosticar valores da fórmula"
                >
                  <FontAwesomeIcon icon={faCalculator} size="sm" />
                </button>
                {!formula.is_active ? (
                  <button
                    onClick={() => handleActivateFormula(formula.id)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                    title="Ativar"
                  >
                    <FontAwesomeIcon icon={faCheck} size="sm" />
                  </button>
                ) : (
                  <button
                    onClick={() => handleDeactivateFormula(formula.id)}
                    className="p-2 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                    title="Desativar"
                  >
                    <FontAwesomeIcon icon={faTimes} size="sm" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };


  // ========================================================================
  // MAIN RENDER
  // ========================================================================

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 text-center">
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          size="2x"
          className="text-blue-600"
        />
        <p className="mt-3 text-neutral-500">Carregando dados...</p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-900">
          <FontAwesomeIcon icon={faCalculator} className="me-2 text-blue-600" />
          Gerenciar Fórmulas de Produção
        </h1>
        <p className="text-neutral-600 text-sm mt-1">
          Configure fórmulas personalizadas para cálculo de sugestões de produção
        </p>
      </div>

      {/* Main Card: Criar + Listar */}
      <div className="bg-white rounded-lg shadow mb-6">
        {/* Card Header com Tabs */}
        <div className="border-b border-neutral-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-neutral-900">
              {editingFormulaId ? '✏️ Editar Fórmula' : '➕ Nova Fórmula'}
            </h2>
            {editingFormulaId && (
              <button
                className="text-xs px-3 py-1 bg-neutral-200 text-neutral-700 rounded hover:bg-neutral-300"
                onClick={handleResetForm}
              >
                Cancelar Edição
              </button>
            )}
          </div>
        </div>

        {/* Card Body: Formulário + Lista */}
        <div className="px-4 py-4">
          {/* Formulário */}
          <div className="mb-8 bg-neutral-50 p-4 rounded-lg border border-neutral-200">
            <h3 className="text-sm font-semibold text-neutral-900 mb-4">
              {editingFormulaId ? '✏️ Editar Fórmula' : '➕ Nova Fórmula'}
            </h3>
            <form onSubmit={handleSubmitFormula} className="space-y-4">
                {/* Produto */}
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-0.5">
                    Produto
                  </label>
                  <select
                    className="w-full px-3 py-1.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                  >
                    <option value="">Todos os produtos (Global)</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Nome */}
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-0.5">
                    Nome
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-1.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                    value={formulaName}
                    onChange={(e) => setFormulaName(e.target.value)}
                    placeholder="Ex: Fórmula Padrão"
                    required
                  />
                </div>

                {/* Descrição */}
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-0.5">
                    Descrição
                  </label>
                  <textarea
                    className="w-full px-3 py-1.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                    rows="1"
                    value={formulaDescription}
                    onChange={(e) => setFormulaDescription(e.target.value)}
                    placeholder="Opcional"
                  />
                </div>

                {/* Fórmula */}
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-0.5">
                    Fórmula
                  </label>
                  <textarea
                    ref={formulaInputRef}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono bg-white"
                    rows="5"
                    value={formulaInput}
                    onChange={(e) => setFormulaInput(e.target.value)}
                    placeholder="Digite a fórmula"
                    required
                  />
                </div>

                {/* Botões */}
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-neutral-400 text-sm font-medium transition-colors"
                >
                  {saving ? '⏳ Salvando...' : editingFormulaId ? '✓ Guardar Alterações' : '➕ Criar Nova Fórmula'}
                </button>
              </form>

              {/* Helper Guide Colapsível */}
              {renderHelper()}
          </div>

          {/* Tabela de Fórmulas */}
          <div className="w-full">
            <h3 className="text-sm font-semibold text-neutral-900 mb-3">
              Fórmulas Criadas ({formulas.length})
            </h3>
            {renderTableSection()}
          </div>
        </div>
      </div>

      {/* Card de Cache de Sugestões */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md border border-blue-200 p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-3 rounded-lg">
              <FontAwesomeIcon icon={faDatabase} className="text-blue-600 text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">
                Cache de Sugestões de Produção
              </h3>
              <p className="text-sm text-neutral-600">
                Atualização automática a cada 1 hora
              </p>
            </div>
          </div>
          <button
            onClick={handleUpdateCache}
            disabled={cacheUpdating || cacheInfo?.is_updating}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
              cacheUpdating || cacheInfo?.is_updating
                ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
            }`}
          >
            <FontAwesomeIcon
              icon={cacheUpdating || cacheInfo?.is_updating ? faSpinner : faRefresh}
              className={cacheUpdating || cacheInfo?.is_updating ? 'animate-spin' : ''}
            />
            <span>
              {cacheUpdating || cacheInfo?.is_updating ? 'Atualizando...' : 'Atualizar Agora'}
            </span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Última Atualização */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <div className="flex items-center space-x-2 mb-2">
              <FontAwesomeIcon icon={faClock} className="text-blue-500" />
              <span className="text-xs font-semibold text-neutral-600 uppercase">
                Última Atualização
              </span>
            </div>
            {cacheLoading ? (
              <div className="text-neutral-400 text-sm">Carregando...</div>
            ) : cacheInfo?.last_update ? (
              <div>
                <div className="text-lg font-bold text-neutral-900">
                  {new Date(cacheInfo.last_update).toLocaleString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  {Math.floor((new Date() - new Date(cacheInfo.last_update)) / 1000 / 60)} min atrás
                </div>
              </div>
            ) : (
              <div className="text-neutral-400 text-sm">-</div>
            )}
          </div>

          {/* Próxima Atualização */}
          <div className="bg-white rounded-lg p-4 border border-green-100">
            <div className="flex items-center space-x-2 mb-2">
              <FontAwesomeIcon icon={faSync} className="text-green-500" />
              <span className="text-xs font-semibold text-neutral-600 uppercase">
                Próxima Atualização
              </span>
            </div>
            {cacheLoading ? (
              <div className="text-neutral-400 text-sm">Carregando...</div>
            ) : cacheInfo?.next_update ? (
              <div>
                <div className="text-lg font-bold text-neutral-900">
                  {new Date(cacheInfo.next_update).toLocaleString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                <div className="text-xs text-green-600 font-semibold mt-1">
                  em {cacheInfo.time_until_next_update_formatted}
                </div>
              </div>
            ) : (
              <div className="text-neutral-400 text-sm">-</div>
            )}
          </div>

          {/* Estatísticas */}
          <div className="bg-white rounded-lg p-4 border border-purple-100">
            <div className="flex items-center space-x-2 mb-2">
              <FontAwesomeIcon icon={faInfoCircle} className="text-purple-500" />
              <span className="text-xs font-semibold text-neutral-600 uppercase">
                Estatísticas
              </span>
            </div>
            {cacheLoading ? (
              <div className="text-neutral-400 text-sm">Carregando...</div>
            ) : cacheInfo ? (
              <div>
                <div className="text-lg font-bold text-neutral-900">
                  {cacheInfo.update_count || 0} atualizações
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  {cacheInfo.is_updating ? (
                    <span className="text-yellow-600 font-semibold">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                      Atualizando...
                    </span>
                  ) : (
                    <span className="text-green-600 font-semibold">
                      <FontAwesomeIcon icon={faCheck} className="mr-1" />
                      Ativo
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-neutral-400 text-sm">-</div>
            )}
          </div>
        </div>
      </div>

      {/* MODAL: CONFIRMAÇÃO DE DELETAR */}
      {formulaToDebug && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">Diagnóstico da Fórmula</h2>
                <p className="text-xs text-neutral-600 mt-1">{formulaToDebug.name}</p>
              </div>
              <button
                type="button"
                className="text-neutral-500 hover:text-neutral-700"
                onClick={() => setFormulaToDebug(null)}
                title="Fechar"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Produto para teste</label>
                <select
                  value={debugProductId}
                  onChange={(event) => setDebugProductId(event.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                >
                  <option value="">Selecione um produto</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>{product.name}</option>
                  ))}
                </select>
              </div>
              <code className="block text-xs bg-neutral-100 px-3 py-2 rounded break-all text-neutral-700">
                {formulaToDebug.formula}
              </code>
              <button
                type="button"
                onClick={handleRunFormulaDebug}
                disabled={debugLoading || !debugProductId}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:bg-neutral-400 text-sm font-medium flex items-center gap-2"
              >
                <FontAwesomeIcon icon={debugLoading ? faSpinner : faCalculator} className={debugLoading ? 'animate-spin' : ''} />
                {debugLoading ? 'Calculando...' : 'Executar diagnóstico'}
              </button>
              {debugResult && (
                <div className="space-y-4 border-t border-neutral-200 pt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="border border-neutral-200 rounded-lg p-3">
                      <div className="text-xs text-neutral-500">Resultado sugerido</div>
                      <div className="text-2xl font-semibold text-neutral-900">{Number(debugResult.result || 0).toFixed(2)} kg</div>
                    </div>
                    <div className="border border-neutral-200 rounded-lg p-3 text-sm text-neutral-700">
                      <div><strong>Produto:</strong> {debugResult.product?.name}</div>
                      <div><strong>Em cura:</strong> {debugResult.product?.is_cured ? 'Sim' : 'Não'}</div>
                      <div><strong>Tempo de cura:</strong> {debugResult.product?.curing_time_days || 0} dias</div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900 mb-2">Valores das variáveis</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {Object.entries(debugResult.variables || {}).map(([name, value]) => (
                        <div key={name} className="border border-neutral-200 rounded px-3 py-2 text-sm">
                          <code className="text-violet-700 break-all block mb-1">{name}</code>
                          <div className="flex justify-between gap-3 text-neutral-700">
                            <span>Usado na fórmula</span>
                            <span className="font-medium text-neutral-900">{value === null ? 'Erro ao calcular' : `${Number(value || 0).toFixed(2)} kg`}</span>
                          </div>
                          {Object.prototype.hasOwnProperty.call(debugResult.unit_variables || {}, name) && (
                            <div className="flex justify-between gap-3 text-neutral-700 mt-1">
                              <span>Pedidos registados</span>
                              <span className="font-medium text-neutral-900">
                                {debugResult.unit_variables[name] === null ? 'Erro ao calcular' : `${Number(debugResult.unit_variables[name] || 0).toFixed(2)} unidades`}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900 mb-2">SQL gerado</h3>
                    <pre className="text-xs bg-neutral-900 text-neutral-100 p-3 rounded overflow-x-auto whitespace-pre-wrap break-words">{debugResult.sql_generated}</pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: CONFIRMAÇÃO DE DELETAR */}
      {showDeleteConfirm && formulaToDelete && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ display: 'flex' }}>
            <div className="bg-white rounded-lg shadow-lg max-w-sm w-full mx-4">
              <div className="border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-neutral-900">Confirmar Exclusão</h2>
                <button
                  type="button"
                  className="text-neutral-500 hover:text-neutral-700"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="px-6 py-4 space-y-3">
                <p className="text-sm text-neutral-700">
                  Tem certeza que deseja deletar a fórmula <strong>{formulaToDelete.name}</strong>?
                </p>
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="border-t border-neutral-200 px-6 py-3 flex gap-2 justify-end">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
                  onClick={confirmDelete}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Deletar
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductionFormulas;
